import { useEffect, useState } from 'react';
import { divisions } from '../data/siteData';
import useSwipe from '../hooks/useSwipe';
import icons from './DivisionIcons';
import Reveal from './Reveal';
import './Divisions.css';

// The backdrop photo stays fixed regardless of which division is active —
// only the panel content on the right rotates.
const HERO_PHOTO = '/images/divisions/Match Flame2.png';

// Rendered one word per line so the static heading stacks the way the
// reference layout does, instead of wrapping naturally at the column edge.
const TITLE_WORDS = ['A', 'Multi-Industry', 'Group,', 'Grounded', 'in', 'Heritage'];

// Left offset, animation duration, and start delay for each drifting
// ember over the photo — same treatment as the Our Story section.
const DIVISION_SPARKS = [
  { left: '15%', duration: '6.8s', delay: '0.3s' },
  { left: '32%', duration: '5.5s', delay: '1.8s' },
  { left: '50%', duration: '7.3s', delay: '0.6s' },
  { left: '68%', duration: '6.1s', delay: '2.6s' },
  { left: '85%', duration: '6.6s', delay: '1.2s' },
];

function Divisions() {
  const [active, setActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const count = divisions.length;
  const d = divisions[active];
  const Icon = icons[d.icon];

  useEffect(() => {
    if (isPaused) return undefined;
    // The automatic timer keeps looping continuously on its own — only
    // manual next/prev (buttons, swipe) stop at the ends.
    const timer = setInterval(() => {
      setActive((a) => (a + 1) % count);
    }, 5000);
    return () => clearInterval(timer);
  }, [count, isPaused]);

  // Bounded, not looping — stops at the first/last division instead of
  // wrapping around, for the arrow buttons, swipe, and auto-rotate alike.
  const prev = () => setActive((a) => Math.max(a - 1, 0));
  const next = () => setActive((a) => Math.min(a + 1, count - 1));
  const swipeHandlers = useSwipe(next, prev);

  return (
    <section
      id="divisions"
      className="section divisions"
      onPointerEnter={(e) => e.pointerType === 'mouse' && setIsPaused(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setIsPaused(false)}
      {...swipeHandlers}
    >
      <Reveal variant="scale" className="division-split">
        <div className="division-split-photo">
          <img src={HERO_PHOTO} alt="Sundaravel Group" />
          <span className="division-sparks" aria-hidden="true">
            {DIVISION_SPARKS.map((s, i) => (
              <span
                key={i}
                className="division-spark"
                style={{ left: s.left, animationDuration: s.duration, animationDelay: s.delay }}
              />
            ))}
          </span>
        </div>

        <div className="division-split-panel">
          <div className="division-split-left">
            <span className="division-split-eyebrow">Our Divisions</span>
            <h3 className="division-split-title">
              {TITLE_WORDS.map((word) => (
                <span key={word} className="division-split-title-word">
                  {word}
                </span>
              ))}
            </h3>
            <span className="division-split-number">{d.number}</span>
          </div>

          <span className="division-split-divider" aria-hidden="true" />

          <div className="division-split-right">
            <div className="division-split-nav">
              <button type="button" onClick={prev} aria-label="Previous division">
                &#8249;
              </button>
              <button type="button" onClick={next} aria-label="Next division">
                &#8250;
              </button>
            </div>

            <div className="division-split-feature" key={d.slug}>
              {Icon && <Icon className="division-split-icon" />}
              <h4 className="division-split-feature-title">{d.title}</h4>
              <span className="division-split-tag">{d.tag}</span>
              <p className="division-split-desc">{d.desc || d.highlights[0]}</p>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export default Divisions;
